import React, { useEffect, useState, useCallback } from "react";
import { Settings2, Loader2, Check, X, Plus, Trash2, Trash, AlertTriangle, CalendarDays, Info, PartyPopper } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Booking Capacity Panel — set the global daily booking cap and per-date
 * overrides. Read-only for users without `bookings.capacity.manage`;
 * editable for admin/hr_head by default or any manager granted the
 * permission via Team Hub → Manage Access.
 *
 * Super Admin also gets a "Clean test bookings" tool beneath the capacity
 * table — dry-run first, confirm to delete (archives to bookings_archive).
 */
const BookingCapacityPanel = ({ authAxios }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    default_daily_limit: 100,
    per_date_overrides: {},
    weekday_rules: {},
    date_range_rules: [],
    can_edit: false,
    actor_role: "",
    updated_by: null,
    updated_at: null,
  });
  const [editingDefault, setEditingDefault] = useState(false);
  const [defaultDraft, setDefaultDraft] = useState(100);
  const [newDate, setNewDate] = useState("");
  const [newLimit, setNewLimit] = useState(50);
  const [showCleanup, setShowCleanup] = useState(false);
  const [weekdayDraft, setWeekdayDraft] = useState({});
  const [editingWeekdays, setEditingWeekdays] = useState(false);
  const [newRange, setNewRange] = useState({ date_from: "", date_to: "", limit: 40, label: "" });

  const fetchCap = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/bookings/capacity`);
      setData(r.data);
      setDefaultDraft(r.data.default_daily_limit || 100);
      setWeekdayDraft(r.data.weekday_rules || {});
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchCap(); }, [fetchCap]);

  const saveDefault = async () => {
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/capacity`, {
        default_daily_limit: Number(defaultDraft),
      });
      setEditingDefault(false);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update");
    }
  };

  const saveWeekdays = async () => {
    // Strip empty entries so clearing a weekday actually removes the rule
    const cleaned = {};
    Object.entries(weekdayDraft).forEach(([k, v]) => {
      if (v !== "" && v !== null && !Number.isNaN(Number(v))) cleaned[k] = Number(v);
    });
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/capacity/weekday-rules`, { rules: cleaned });
      setEditingWeekdays(false);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save weekday rules");
    }
  };

  const addRangeRule = async () => {
    if (!newRange.date_from || !newRange.date_to) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/bookings/capacity/range-rules`, {
        date_from: newRange.date_from,
        date_to: newRange.date_to,
        limit: Number(newRange.limit),
        label: newRange.label,
      });
      setNewRange({ date_from: "", date_to: "", limit: 40, label: "" });
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add range rule");
    }
  };

  const deleteRangeRule = async (id) => {
    if (!window.confirm("Delete this range rule?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/bookings/capacity/range-rules/${id}`);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleRangeRule = async (id) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/capacity/range-rules/${id}/toggle`);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  // Quick-apply: sets the range rule for an entire calendar month (YYYY-MM)
  const quickSetMonth = async () => {
    const ym = window.prompt("Month (YYYY-MM):", new Date().toISOString().slice(0, 7));
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return;
    const lim = window.prompt(`Daily cap for the whole of ${ym}:`, "10");
    if (!lim) return;
    const [y, m] = ym.split("-").map(Number);
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    try {
      await authAxios.post(`${API_URL}/api/admin/bookings/capacity/range-rules`, {
        date_from: `${ym}-01`,
        date_to: `${ym}-${String(lastDay).padStart(2, "0")}`,
        limit: Number(lim),
        label: `Bulk · entire ${ym}`,
      });
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  // Quick-apply: set "all Sundays" (or any weekday) in one click
  const quickSetAllWeekday = (wkIdx, defaultLim) => async () => {
    const lim = window.prompt(`Daily cap for ALL ${WEEKDAY_LABELS[wkIdx]}days:`, String(defaultLim || 10));
    if (!lim) return;
    const next = { ...(data.weekday_rules || {}), [String(wkIdx)]: Number(lim) };
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/capacity/weekday-rules`, { rules: next });
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  // Import the curated Indian public holidays list as range rules for a given year
  const importHolidays = async () => {
    const year = window.prompt(
      "Import Indian festive/holiday range rules for which year? (supported: 2026, 2027)",
      String(new Date().getUTCFullYear()),
    );
    if (!year) return;
    const y = parseInt(year, 10);
    if (!y || y < 2026 || y > 2027) {
      alert("Only 2026 and 2027 are supported in the curated data.");
      return;
    }
    // Preview first so the admin knows what's about to happen
    let preview;
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/bookings/capacity/holidays/preview`, { params: { year: y } });
      preview = r.data;
    } catch (e) {
      alert(e?.response?.data?.detail || "Preview failed");
      return;
    }
    const boostCount = (preview.items || []).filter((i) => i.behaviour === "boost").length;
    const damperCount = (preview.items || []).filter((i) => i.behaviour === "damper").length;
    const confirm = window.confirm(
      `Import ${preview.items.length} holiday rules for ${y}?\n` +
      `• ${boostCount} boost days (e.g. Diwali, NYE, Independence Day)\n` +
      `• ${damperCount} damper days (e.g. Holi, dry days)\n` +
      `Default cap detected: ${preview.default_cap}\n` +
      `Boost → ${Math.round(preview.default_cap * 1.5)} · Damper → ${Math.max(1, Math.round(preview.default_cap * 0.4))}\n\n` +
      `Existing rules with matching labels will be skipped (idempotent).`,
    );
    if (!confirm) return;
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/bookings/capacity/holidays/import`, {}, { params: { year: y } });
      alert(`Imported ${r.data.created_count} holiday rules.\nSkipped ${r.data.skipped_count} (already existed or neutral).`);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Import failed");
    }
  };

  const addOverride = async () => {
    if (!newDate || !newLimit) return;
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/capacity/overrides/${newDate}`, {
        limit: Number(newLimit),
      });
      setNewDate("");
      setNewLimit(50);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add override");
    }
  };

  const clearOverride = async (date) => {
    try {
      await authAxios.delete(`${API_URL}/api/admin/bookings/capacity/overrides/${date}`);
      await fetchCap();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to clear");
    }
  };

  const overrides = Object.entries(data.per_date_overrides || {}).sort();
  const isSuper = data.actor_role === "admin" || data.actor_role === "super_admin";

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4" data-testid="booking-capacity-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#10B981] flex items-center justify-center">
          <Settings2 size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#1E1B4B] text-sm">Daily Booking Capacity</h3>
          <p className="text-xs text-[#94a3b8]">
            Global cap + per-date overrides. {data.can_edit ? "You can edit." : "View only — ask an admin for the `bookings.capacity.manage` permission to change."}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : (
        <>
          {/* Global default */}
          <div className="rounded-xl border border-gray-100 p-3 mb-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Global daily limit</p>
              {editingDefault ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number" min="0" max="1000"
                    value={defaultDraft}
                    onChange={(e) => setDefaultDraft(e.target.value)}
                    className="w-28 px-2 py-1 border border-gray-200 rounded-lg text-sm"
                    data-testid="default-limit-input"
                  />
                  <button onClick={saveDefault}
                    className="px-2 py-1 rounded bg-[#E83E8C] text-white text-xs font-bold flex items-center gap-1"
                    data-testid="save-default-limit"
                  ><Check size={12} /> Save</button>
                  <button onClick={() => { setEditingDefault(false); setDefaultDraft(data.default_daily_limit); }}
                    className="px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs"><X size={12} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-3 mt-0.5">
                  <p className="text-2xl font-extrabold text-[#1E1B4B]" data-testid="default-limit-value">
                    {data.default_daily_limit}
                    <span className="text-xs font-semibold text-[#94a3b8] ml-1">bookings / day</span>
                  </p>
                  {data.can_edit && (
                    <button onClick={() => setEditingDefault(true)}
                      className="text-[11px] text-[#E83E8C] font-bold hover:underline"
                      data-testid="edit-default-limit">Edit</button>
                  )}
                </div>
              )}
              {data.updated_by && (
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Last changed by <span className="font-mono">{data.updated_by}</span> · {data.updated_at?.slice(0, 16).replace("T", " ")}
                </p>
              )}
            </div>
          </div>

          {/* Weekday rules */}
          <div className="rounded-xl border border-gray-100 p-3 mb-3" data-testid="weekday-rules-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1">
                <CalendarDays size={12} /> Weekday rules
              </p>
              {data.can_edit && !editingWeekdays && (
                <button onClick={() => setEditingWeekdays(true)}
                  className="text-[11px] text-[#E83E8C] font-bold hover:underline"
                  data-testid="edit-weekday-rules">Edit</button>
              )}
              {data.can_edit && editingWeekdays && (
                <div className="flex gap-1">
                  <button onClick={saveWeekdays}
                    className="px-2 py-0.5 rounded bg-[#E83E8C] text-white text-[10px] font-bold flex items-center gap-0.5"
                    data-testid="save-weekday-rules">
                    <Check size={10} /> Save
                  </button>
                  <button onClick={() => { setEditingWeekdays(false); setWeekdayDraft(data.weekday_rules || {}); }}
                    className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">
                    <X size={10} />
                  </button>
                </div>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mb-2 flex items-center gap-1">
              <Info size={10} /> Applies to every matching weekday. Empty = use global default. Per-date overrides &amp; date ranges still win.
            </p>
            <div className="grid grid-cols-7 gap-1.5">
              {WEEKDAY_LABELS.map((lbl, idx) => {
                const key = String(idx);
                const current = (data.weekday_rules || {})[key];
                const draftVal = weekdayDraft[key] ?? "";
                return (
                  <div key={key} className="text-center">
                    <p className="text-[10px] font-bold text-gray-500 mb-0.5">{lbl}</p>
                    {editingWeekdays ? (
                      <input type="number" min="0" max="1000"
                        value={draftVal}
                        onChange={(e) => setWeekdayDraft({ ...weekdayDraft, [key]: e.target.value })}
                        placeholder="—"
                        className="w-full px-1 py-1 text-[11px] border border-gray-200 rounded text-center"
                        data-testid={`weekday-input-${key}`} />
                    ) : data.can_edit ? (
                      <button onClick={quickSetAllWeekday(idx, current)}
                        className={`w-full py-1 rounded text-[11px] font-semibold ${current != null ? "bg-indigo-50 text-indigo-700" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}
                        data-testid={`weekday-tile-${key}`}
                        title={`Click to set cap for all ${lbl}days`}>
                        {current != null ? current : "—"}
                      </button>
                    ) : (
                      <div className={`py-1 rounded text-[11px] font-semibold ${current != null ? "bg-indigo-50 text-indigo-700" : "bg-gray-50 text-gray-400"}`}>
                        {current != null ? current : "—"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date-range rules */}
          <div className="rounded-xl border border-gray-100 p-3 mb-3" data-testid="range-rules-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1">
                <CalendarDays size={12} /> Date-range rules
              </p>
              {data.can_edit && (
                <div className="flex items-center gap-3">
                  <button onClick={importHolidays}
                    className="text-[11px] font-bold text-pink-600 hover:underline flex items-center gap-0.5"
                    data-testid="import-holidays-btn">
                    <PartyPopper size={10} /> Import holidays…
                  </button>
                  <button onClick={quickSetMonth}
                    className="text-[11px] font-bold text-indigo-600 hover:underline flex items-center gap-0.5"
                    data-testid="quick-set-month-btn">
                    <Plus size={10} /> Whole month…
                  </button>
                </div>
              )}
            </div>
            {(data.date_range_rules || []).length === 0 && (
              <p className="text-xs text-[#94a3b8] text-center py-2">No range rules. Good for monsoon slowdowns, festive surges, or internal offsites.</p>
            )}
            {(data.date_range_rules || []).length > 0 && (
              <div className="space-y-1 mb-2">
                {data.date_range_rules.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-[11px]" data-testid={`range-row-${r.id}`}>
                    <span className="font-mono text-gray-600 w-44 truncate">{r.date_from} → {r.date_to}</span>
                    <span className="font-bold text-[#1E1B4B]">{r.limit} <span className="font-normal text-gray-400">cap</span></span>
                    {r.label && <span className="text-gray-500 italic truncate">{r.label}</span>}
                    <span className="flex-1" />
                    {data.can_edit && (
                      <>
                        <button onClick={() => toggleRangeRule(r.id)}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
                          data-testid={`toggle-range-${r.id}`}>
                          {r.active ? "Active" : "Paused"}
                        </button>
                        <button onClick={() => deleteRangeRule(r.id)}
                          className="text-gray-400 hover:text-red-500"
                          data-testid={`delete-range-${r.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {data.can_edit && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <input type="date" value={newRange.date_from}
                  onChange={(e) => setNewRange({ ...newRange, date_from: e.target.value })}
                  className="px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="range-from-input" />
                <input type="date" value={newRange.date_to}
                  onChange={(e) => setNewRange({ ...newRange, date_to: e.target.value })}
                  className="px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="range-to-input" />
                <input type="number" min="0" max="1000" value={newRange.limit}
                  onChange={(e) => setNewRange({ ...newRange, limit: e.target.value })}
                  placeholder="cap"
                  className="w-20 px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="range-limit-input" />
                <input value={newRange.label}
                  onChange={(e) => setNewRange({ ...newRange, label: e.target.value })}
                  placeholder="label (e.g. Monsoon)"
                  className="flex-1 min-w-[120px] px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="range-label-input" />
                <button onClick={addRangeRule} disabled={!newRange.date_from || !newRange.date_to}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-[#E83E8C] text-white text-[11px] font-bold disabled:opacity-50"
                  data-testid="add-range-btn">
                  <Plus size={12} /> Add
                </button>
              </div>
            )}
          </div>

          {/* Per-date overrides */}
          <div className="rounded-xl border border-gray-100 p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Per-date overrides</p>
              <span className="text-[10px] text-gray-400">{overrides.length} active</span>
            </div>
            {overrides.length === 0 && (
              <p className="text-xs text-[#94a3b8] text-center py-2">No overrides. Booking dates use the global limit.</p>
            )}
            {overrides.length > 0 && (
              <div className="space-y-1 mb-2">
                {overrides.map(([date, limit]) => (
                  <div key={date} className="flex items-center gap-2 text-xs" data-testid={`override-row-${date}`}>
                    <span className="font-mono text-gray-600 w-28">{date}</span>
                    <span className="font-bold text-[#1E1B4B]">{limit} <span className="font-normal text-gray-400">bookings</span></span>
                    <span className="flex-1" />
                    {data.can_edit && (
                      <button onClick={() => clearOverride(date)}
                        className="text-gray-400 hover:text-red-500"
                        data-testid={`clear-override-${date}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {data.can_edit && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="override-date-input" />
                <input type="number" min="0" max="1000" value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  placeholder="limit"
                  className="w-24 px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  data-testid="override-limit-input" />
                <button onClick={addOverride} disabled={!newDate}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-[#E83E8C] text-white text-[11px] font-bold disabled:opacity-50"
                  data-testid="add-override-btn">
                  <Plus size={12} /> Set override
                </button>
              </div>
            )}
          </div>

          {/* Super-admin only: bulk cleanup */}
          {isSuper && (
            <button onClick={() => setShowCleanup(true)}
              className="w-full py-2 rounded-xl border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 flex items-center justify-center gap-1.5"
              data-testid="open-cleanup-modal">
              <Trash size={12} /> Remove test / unwanted bookings…
            </button>
          )}
        </>
      )}

      {showCleanup && (
        <CleanupModal authAxios={authAxios} onClose={() => setShowCleanup(false)} />
      )}
    </div>
  );
};

const CleanupModal = ({ authAxios, onClose }) => {
  const [filters, setFilters] = useState({
    name_contains: "",
    phone_contains: "",
    date_from: "",
    date_to: "",
    status_in: [],
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(null);

  const hasFilter = filters.name_contains || filters.phone_contains || filters.date_from || filters.date_to || filters.status_in.length > 0;

  const runPreview = async () => {
    setLoading(true); setDeleted(null);
    try {
      const body = { ...filters };
      if (!body.status_in.length) delete body.status_in;
      const r = await authAxios.post(`${API_URL}/api/admin/bookings/cleanup`, body);
      setPreview(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Preview failed");
    }
    setLoading(false);
  };

  const confirmDelete = async () => {
    if (!preview || !window.confirm(`Permanently delete ${preview.matched_total} booking(s)? They'll be archived to bookings_archive.`)) return;
    setLoading(true);
    try {
      const body = { ...filters, confirm: true };
      if (!body.status_in.length) delete body.status_in;
      const r = await authAxios.post(`${API_URL}/api/admin/bookings/cleanup`, body);
      setDeleted(r.data.deleted);
      setPreview(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
    setLoading(false);
  };

  const toggleStatus = (s) => {
    setFilters((f) => ({
      ...f,
      status_in: f.status_in.includes(s) ? f.status_in.filter((x) => x !== s) : [...f.status_in, s],
    }));
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="cleanup-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1E1B4B] flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" /> Clean test / spam bookings
          </h3>
          <button onClick={onClose} className="text-gray-400"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mb-3">
          Deletes real rows from the bookings table (archived to <span className="font-mono">bookings_archive</span> first). Always preview before confirming. Max 200 rows per call.
        </p>
        <div className="space-y-2 text-xs">
          <div>
            <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Customer name contains</label>
            <input value={filters.name_contains} onChange={(e) => setFilters({ ...filters, name_contains: e.target.value })}
              placeholder="e.g. test, dummy, regtest"
              className="w-full px-2 py-1.5 border rounded-lg"
              data-testid="cleanup-name-filter" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Phone contains</label>
            <input value={filters.phone_contains} onChange={(e) => setFilters({ ...filters, phone_contains: e.target.value })}
              placeholder="e.g. 9999 or full number"
              className="w-full px-2 py-1.5 border rounded-lg"
              data-testid="cleanup-phone-filter" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Date from</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-lg" data-testid="cleanup-date-from" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Date to</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-lg" data-testid="cleanup-date-to" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Status (leave empty = all)</label>
            <div className="flex flex-wrap gap-1">
              {["booked", "cancelled", "rejected", "completed"].map((s) => (
                <button key={s} type="button" onClick={() => toggleStatus(s)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-semibold capitalize ${filters.status_in.includes(s) ? "bg-[#E83E8C] text-white" : "bg-gray-100 text-gray-600"}`}
                  data-testid={`cleanup-status-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={runPreview} disabled={!hasFilter || loading}
            className="flex-1 py-2 rounded-xl bg-gray-100 text-[#1E1B4B] text-xs font-bold disabled:opacity-50"
            data-testid="cleanup-preview-btn">
            {loading ? <Loader2 size={12} className="animate-spin inline" /> : "Preview matches"}
          </button>
        </div>

        {deleted !== null && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700">
            ✅ Deleted {deleted} booking(s). Archived to <span className="font-mono">bookings_archive</span>.
          </div>
        )}

        {preview && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50/50 p-3">
            <p className="text-xs font-bold text-red-700 mb-1">
              Matched {preview.matched_total} booking(s) — showing first {preview.showing}
            </p>
            <div className="max-h-56 overflow-y-auto space-y-1 mb-2">
              {(preview.items || []).map((b) => (
                <div key={b.id} className="flex gap-2 text-[11px] border-b border-red-100 py-1">
                  <span className="font-mono text-gray-500 w-28 truncate">{b.id}</span>
                  <span className="flex-1 truncate">{b.customer_name}</span>
                  <span className="text-gray-400">{b.customer_phone}</span>
                  <span className="font-mono">{b.date}</span>
                </div>
              ))}
            </div>
            <button onClick={confirmDelete} disabled={loading || preview.matched_total === 0}
              className="w-full py-2 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 disabled:opacity-50"
              data-testid="cleanup-confirm-btn">
              {loading ? <Loader2 size={12} className="animate-spin inline" /> : `Delete ${preview.matched_total} booking(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingCapacityPanel;
